<template lang="pug">
  yz-dialog(v-model="showDialog" persistent scrollable max-width="600px")
    yz-card
      yz-dialog-normal-header(:title="dialogTitle" @close="onCancel")

      yz-dialog-normal-content.px-6.py-4
        // Restricted state
        template(v-if="isRestricted")
          .d-flex.align-center.text-neutral-700
            yz-icon.mr-2(color="yz-danger-500" size="18") $yzi-alert-circle
            .yz-medium-body-md {{ $t('registration_page.cancel_visit.restriction_not_allowed') }}

          .yz-regular-body-sm.mt-2.text-neutral-700 {{ restrictionReason }}

          // Linked documents section
          .mt-4
            .yz-medium-body-xs.text-neutral-700 {{ $t('registration_page.cancel_visit.linked_documents') }}

            yz-card.mt-2.pa-3(outlined, color="yz-neutral-200" style="border-radius: 10px;")
              .d-flex(style="gap: 12px;")
                .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.invoice_number') }}
                .yz-medium-body-sm.text-neutral-700 {{ $t('invoice') }} {{ '#' + parentRegistration.rid }}

              template(v-if="hasClaim && !hasMultipleClaims")
                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.panel_claim_number') }}
                  .d-flex.align-center.text-neutral-700(style="gap: 8px;")
                    .yz-medium-body-sm {{ $t('panel.claim') }} {{ '#' + claimData.panel_claim.rid }}
                    claim-status-badge(:status="claimData.claim_status")

                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.claim_amount') }}
                  .yz-medium-body-sm.text-neutral-700 {{ claimData.amount | displayPrice }}

        // Normal cancellation state
        template(v-else)
          .d-flex.align-center.text-neutral-700
            yz-icon.mr-2(color="yz-danger-500" size="18") $yzi-alert-circle
            .yz-medium-body-md {{ $t('registration_page.cancel_visit.warning_message') }}

          .yz-regular-body-sm.mt-2.text-neutral-700 {{ $t('registration_page.cancel_visit.subtitle') }}

          // Linked documents section
          .mt-4
            .yz-medium-body-xs.text-neutral-700 {{ $t('registration_page.cancel_visit.linked_documents') }}

            yz-card.mt-2.pa-3(outlined, color="yz-neutral-200" style="border-radius: 10px;")
              .d-flex(style="gap: 12px;")
                .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.invoice_number') }}
                .yz-medium-body-sm.text-neutral-700 {{ $t('invoice') }} {{ '#' + parentRegistration.rid }}

              // Claim details - Single type
              template(v-if="hasClaim && !isBulkClaim")
                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.panel_claim_number') }}
                  .d-flex.align-center.text-neutral-700(style="gap: 8px;")
                    .yz-medium-body-sm {{ $t('panel.claim') }} {{ '#' + claimData.panel_claim.rid }}
                    claim-status-badge(:status="claimData.claim_status")

                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.claim_amount') }}
                  .yz-medium-body-sm.text-neutral-700 {{ claimData.amount | displayPrice }}

              // Claim details - Bulk type
              template(v-if="hasClaim && isBulkClaim")
                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.panel_claim_number') }}
                  .d-flex.align-center.text-neutral-700(style="gap: 8px;")
                    .yz-medium-body-sm {{ $t('panel.claim') }} {{ '#' + claimData.panel_claim.rid }}
                    claim-status-badge(:status="claimData.claim_status")

                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.current_claimed_amount') }}
                  .yz-medium-body-sm.text-neutral-700 {{ claimData.panel_claim.submitted_amount | displayPrice }}

                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.new_claim_amount') }}
                  .yz-medium-body-sm.text-neutral-700 {{ (claimData.panel_claim.submitted_amount - claimData.amount) | displayPrice }}

                .d-flex.mt-3(style="gap: 12px;")
                  .yz-medium-body-sm.text-neutral-500(style="width: 180px;") {{ $t('registration_page.cancel_visit.total_adjustments') }}
                  .yz-medium-body-sm.text-danger-500 {{ claimData.amount ? '-' : '' }} {{ claimData.amount | displayPrice }}

      yz-dialog-normal-actions
        template(v-if="isRestricted")
          yz-btn(size="lg" text, :label="$t('registration_page.cancel_visit.close')" @click="onCancel")
        template(v-else)
          yz-btn(size="lg" text :label="$t('cancel')" :disabled="loading" @click="onCancel")
          yz-btn(size="lg" :label="$t('confirm')" color="danger" :loading="loading" @click="onConfirm")
</template>

<script>
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'

export default {
  components: { ClaimStatusBadge },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    linkedDocuments: {
      type: Object,
      default: () => ({})
    },
    selectedVisit: {
      type: Object,
      default: () => ({})
    },
    parentRegistration: {
      type: Object,
      required: true
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    hasClaim() {
      return this.parentRegistration?.claimed_payments?.length > 0
    },
    claimData() {
      if (this.hasClaim) {
        return this.parentRegistration.claimed_payments[0]
      }
      return null
    },
    isBulkClaim() {
      return this.claimData?.panel_claim?.type === 'bulk'
    },
    hasMultipleClaims() {
      return this.parentRegistration?.claimed_payments?.length > 1
    },
    isClaimApprovedOrReceived() {
      if (!this.hasClaim) return false
      const status = this.claimData.claim_status?.toLowerCase()
      return status === 'approved' || status === 'received'
    },
    isRestricted() {
      return this.hasMultipleClaims || this.isClaimApprovedOrReceived
    },
    restrictionReason() {
      if (this.hasMultipleClaims) {
        return this.$t('registration_page.cancel_visit.restriction_multiple_claims')
      }
      if (this.isClaimApprovedOrReceived) {
        const status = this.claimData.claim_status
        return this.$t('registration_page.cancel_visit.restriction_approved_received', { status })
      }
      return ''
    },
    dialogTitle() {
      return this.isRestricted ? this.$t('registration_page.cancel_visit.restriction_title') : this.$t('registration_page.cancel_visit.title')
    }
  },
  methods: {
    onCancel() {
      this.$emit('canceled')
      this.showDialog = false
    },
    onConfirm() {
      this.$emit('confirmed', this.selectedVisit)
    }
  }
}
</script>
